import { useState, useEffect } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { TrendingUp, TrendingDown, Building2, ExternalLink, Plus, Minus } from 'lucide-react'
import { api } from '@/lib/api'
import StockSearch from '@/components/trading/StockSearch'
import TradeModal from '@/components/trading/TradeModal'
import { formatCurrency, formatPercent, formatLargeNumber, cn } from '@/lib/utils'
import type { StockQuote, CompanyProfile, Holding, SearchResult } from '@/types'

export default function Trade() {
  const { symbol: routeSymbol } = useParams()
  const navigate = useNavigate()

  const [symbol, setSymbol] = useState(routeSymbol?.toUpperCase() ?? '')
  const [quote, setQuote] = useState<StockQuote | null>(null)
  const [profile, setProfile] = useState<CompanyProfile | null>(null)
  const [holding, setHolding] = useState<Holding | null>(null)
  const [isLoading, setIsLoading] = useState(false)
  const [tradeModal, setTradeModal] = useState<{ type: 'buy' | 'sell' } | null>(null)

  const fetchQuote = async (sym: string) => {
    setIsLoading(true)
    setQuote(null)
    setProfile(null)
    setHolding(null)

    const [quoteRes, portfolioRes] = await Promise.all([
      api.getQuote(sym),
      api.getPortfolio(),
    ])

    if (quoteRes.success && quoteRes.data) {
      setQuote(quoteRes.data.quote)
      setProfile(quoteRes.data.profile)
    }

    if (portfolioRes.success && portfolioRes.data) {
      const h = portfolioRes.data.holdings.find((x) => x.symbol === sym)
      setHolding(h ?? null)
    }

    setIsLoading(false)
  }

  useEffect(() => {
    if (routeSymbol) {
      setSymbol(routeSymbol.toUpperCase())
      fetchQuote(routeSymbol.toUpperCase())
    }
  }, [routeSymbol])

  const handleSearch = (result: SearchResult) => {
    navigate(`/trade/${result.symbol}`)
  }

  const handleTradeSuccess = (newBalance: number) => {
    setTradeModal(null)
    // Re-fetch to update holding info
    if (symbol) fetchQuote(symbol)
    void newBalance
  }

  const isPositive = (quote?.changePercent ?? 0) >= 0

  return (
    <div className="space-y-6 animate-fade-in">
      <div>
        <h1 className="text-2xl font-bold">Trade</h1>
        <p className="text-white/40 text-sm mt-0.5">Search and execute orders</p>
      </div>

      {/* Search */}
      <div className="max-w-md">
        <StockSearch
          onSelect={handleSearch}
          placeholder="Search by ticker or company name..."
        />
      </div>

      {isLoading && (
        <div className="card p-8 flex items-center justify-center">
          <div className="w-6 h-6 border-2 border-accent border-t-transparent rounded-full animate-spin" />
        </div>
      )}

      {!isLoading && !quote && !routeSymbol && (
        <div className="card p-12 text-center">
          <div className="w-14 h-14 bg-white/[0.04] rounded-2xl flex items-center justify-center mx-auto mb-4">
            <TrendingUp className="w-7 h-7 text-white/20" />
          </div>
          <h3 className="font-semibold text-white/60 mb-1">Search for a stock</h3>
          <p className="text-white/30 text-sm">Enter a ticker symbol or company name above</p>
          <div className="mt-6 flex flex-wrap gap-2 justify-center">
            {['AAPL', 'MSFT', 'GOOGL', 'AMZN', 'NVDA', 'TSLA'].map((s) => (
              <button
                key={s}
                onClick={() => navigate(`/trade/${s}`)}
                className="px-3 py-1.5 bg-white/[0.06] hover:bg-white/[0.1] rounded-lg text-xs font-mono font-medium transition-colors"
              >
                {s}
              </button>
            ))}
          </div>
        </div>
      )}

      {!isLoading && quote && (
        <div className="grid lg:grid-cols-3 gap-6">
          {/* Quote card */}
          <div className="lg:col-span-2 space-y-4">
            <div className="card p-6">
              <div className="flex items-start justify-between mb-6">
                <div>
                  <div className="flex items-center gap-3 mb-1">
                    <h2 className="text-2xl font-bold">{quote.symbol}</h2>
                    {profile && (
                      <span className="text-sm text-white/40">{profile.exchange}</span>
                    )}
                  </div>
                  {profile && (
                    <div className="text-white/50 text-sm">{profile.name}</div>
                  )}
                </div>
                {profile?.logo && (
                  <img
                    src={profile.logo}
                    alt={profile.name}
                    className="w-10 h-10 rounded-xl object-contain bg-white/[0.06] p-1"
                    onError={(e) => { (e.target as HTMLImageElement).style.display = 'none' }}
                  />
                )}
              </div>

              <div className="flex items-end gap-4">
                <div className="text-4xl font-bold font-mono">
                  {formatCurrency(quote.price)}
                </div>
                <div
                  className={cn(
                    'flex items-center gap-1.5 mb-1 text-sm font-semibold',
                    isPositive ? 'text-gain' : 'text-loss'
                  )}
                >
                  {isPositive ? (
                    <TrendingUp className="w-4 h-4" />
                  ) : (
                    <TrendingDown className="w-4 h-4" />
                  )}
                  {isPositive ? '+' : ''}{formatCurrency(quote.change)} ({formatPercent(quote.changePercent)})
                </div>
              </div>

              {/* Stats row */}
              <div className="grid grid-cols-4 gap-3 mt-6">
                {[
                  { label: 'Open', value: formatCurrency(quote.open) },
                  { label: 'High', value: formatCurrency(quote.high) },
                  { label: 'Low', value: formatCurrency(quote.low) },
                  { label: 'Prev Close', value: formatCurrency(quote.prevClose) },
                ].map(({ label, value }) => (
                  <div key={label} className="bg-white/[0.03] rounded-xl p-3">
                    <div className="text-xs text-white/30 mb-1">{label}</div>
                    <div className="text-sm font-mono font-medium">{value}</div>
                  </div>
                ))}
              </div>
            </div>

            {/* Company info */}
            {profile && (
              <div className="card p-6">
                <div className="flex items-center gap-2 mb-4">
                  <Building2 className="w-4 h-4 text-white/30" />
                  <h3 className="font-medium text-sm">Company Info</h3>
                </div>
                <div className="grid grid-cols-2 gap-4 text-sm">
                  {profile.industry && (
                    <div>
                      <div className="text-white/30 text-xs mb-0.5">Industry</div>
                      <div>{profile.industry}</div>
                    </div>
                  )}
                  {profile.marketCap > 0 && (
                    <div>
                      <div className="text-white/30 text-xs mb-0.5">Market Cap</div>
                      <div>{formatLargeNumber(profile.marketCap * 1e6)}</div>
                    </div>
                  )}
                </div>
                {profile.weburl && (
                  <a
                    href={profile.weburl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-1.5 text-xs text-accent hover:underline mt-4"
                  >
                    <ExternalLink className="w-3 h-3" />
                    {profile.weburl.replace(/^https?:\/\//, '').replace(/\/$/, '')}
                  </a>
                )}
              </div>
            )}
          </div>

          {/* Trade actions */}
          <div className="space-y-4">
            <div className="card p-5 space-y-3">
              <h3 className="font-semibold text-sm">Place Order</h3>

              <button
                onClick={() => setTradeModal({ type: 'buy' })}
                className="w-full flex items-center justify-center gap-2 py-3 bg-gain text-black font-semibold rounded-xl hover:brightness-105 transition-all text-sm"
              >
                <Plus className="w-4 h-4" strokeWidth={2.5} />
                Buy {quote.symbol}
              </button>

              {holding && holding.shares > 0 && (
                <button
                  onClick={() => setTradeModal({ type: 'sell' })}
                  className="w-full flex items-center justify-center gap-2 py-3 bg-loss text-white font-semibold rounded-xl hover:brightness-105 transition-all text-sm"
                >
                  <Minus className="w-4 h-4" strokeWidth={2.5} />
                  Sell {quote.symbol}
                </button>
              )}
            </div>

            {holding && (
              <div className="card p-5 space-y-3">
                <h3 className="font-semibold text-sm text-white/60">Your Position</h3>
                {[
                  { label: 'Shares owned', value: holding.shares.toLocaleString() },
                  { label: 'Avg cost', value: formatCurrency(holding.avg_cost_basis) },
                  { label: 'Current value', value: formatCurrency(holding.total_value ?? 0) },
                  {
                    label: 'Unrealized P/L',
                    value: formatCurrency(holding.unrealized_pnl ?? 0),
                    positive: (holding.unrealized_pnl ?? 0) >= 0,
                    negative: (holding.unrealized_pnl ?? 0) < 0,
                  },
                ].map(({ label, value, positive, negative }) => (
                  <div key={label} className="flex justify-between text-sm">
                    <span className="text-white/40">{label}</span>
                    <span
                      className={cn(
                        'font-mono font-medium',
                        positive ? 'text-gain' : negative ? 'text-loss' : ''
                      )}
                    >
                      {value}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {!isLoading && !quote && routeSymbol && (
        <div className="card p-8 text-center text-white/40">
          Symbol "{routeSymbol.toUpperCase()}" not found. Try another ticker.
        </div>
      )}

      {tradeModal && quote && (
        <TradeModal
          symbol={quote.symbol}
          quote={quote}
          defaultType={tradeModal.type}
          currentShares={holding?.shares ?? 0}
          onClose={() => setTradeModal(null)}
          onSuccess={handleTradeSuccess}
        />
      )}
    </div>
  )
}
